import { inject } from '@angular/core';
import type {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpEvent
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { isPublicApiEndpoint } from '../constants/public-endpoints';

export const authErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors - token might be expired
      if (error.status === 401 && !req.url.includes('login') && !isPublicApiEndpoint(req.url)) {
        return handle401Error(req, next, authService);
      }

      // Let other interceptors handle other types of errors
      return throwError(() => error);
    })
  );
};

// Helper function to handle 401 errors by refreshing token
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  // Create observable that attempts token refresh
  return new Observable((observer) => {
    authService.refreshToken().subscribe({
      next: (refreshed: boolean) => {
        if (refreshed) {
          // Token refreshed successfully, retry original request with new token
          const authReq = request.clone({
            setHeaders: {
              Authorization: authService.getAuthHeader()
            }
          });

          // Execute the request again with new token
          next(authReq).subscribe({
            next: (event) => observer.next(event),
            error: (err) => observer.error(err),
            complete: () => observer.complete()
          });
        } else {
          // Token refresh failed, logout user
          authService.logout().then(() => {
            observer.error('Authentication failed');
          });
        }
      },
      error: (err: unknown) => {
        authService.logout().then(() => {
          observer.error(err);
        });
      }
    });
  });
}
